import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
    ChevronLeft, Plus, Trash2, Sparkles, DollarSign, FileText,
    RefreshCw, Save, Send, Building2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultItem = { product_name: '', description: '', quantity: 1, unit_price: 0, discount_percent: 0, total: 0 };

export default function ProposalCreator() {
    const { proposalId } = useParams();
    const [searchParams] = useSearchParams();
    const { getAuthHeader } = useAuth();
    const navigate = useNavigate();
    const isEdit = !!proposalId;

    // Pre-fill account from URL query params (e.g. from Account Detail page)
    const prefillAccountId = searchParams.get('account_id') || '';
    const prefillAccountName = searchParams.get('account_name') || '';

    const [form, setForm] = useState({
        title: prefillAccountName ? `Proposal for ${prefillAccountName} — ${new Date().getFullYear()}` : '',
        account_id: prefillAccountId,
        account_name: prefillAccountName,
        deal_id: '',
        line_items: [{ ...defaultItem }],
        tax_rate: 0, valid_until: '', cover_letter: '', notes: '', terms: '',
        status: 'draft',
    });
    const [accounts, setAccounts] = useState([]);
    const [deals, setDeals] = useState([]);
    const [loading, setLoading] = useState(false);
    const [generatingAI, setGeneratingAI] = useState(false);

    useEffect(() => {
        loadAccounts();
        if (isEdit) loadProposal();
    }, [proposalId]);

    const loadAccounts = async () => {
        try {
            const [aRes, dRes] = await Promise.all([
                axios.get(`${API}/accounts`, { headers: getAuthHeader() }),
                axios.get(`${API}/deals`, { headers: getAuthHeader() }),
            ]);
            setAccounts(Array.isArray(aRes.data) ? aRes.data : aRes.data.data || []);
            setDeals(dRes.data);
        } catch {}
    };

    const loadProposal = async () => {
        try {
            const res = await axios.get(`${API}/proposals/${proposalId}`, { headers: getAuthHeader() });
            setForm(res.data);
        } catch { toast.error('Proposal not found'); navigate('/proposals'); }
    };

    const calcTotals = (items, taxRate) => {
        const subtotal = items.reduce((s, i) => {
            const t = (i.quantity || 0) * (i.unit_price || 0) * (1 - (i.discount_percent || 0) / 100);
            return s + t;
        }, 0);
        const tax = subtotal * (taxRate || 0) / 100;
        return { subtotal, tax, total: subtotal + tax };
    };

    const updateItem = (idx, key, val) => {
        const items = [...form.line_items];
        items[idx] = { ...items[idx], [key]: val };
        items[idx].total = (items[idx].quantity || 0) * (items[idx].unit_price || 0) * (1 - (items[idx].discount_percent || 0) / 100);
        setForm({ ...form, line_items: items });
    };

    const addItem = () => setForm({ ...form, line_items: [...form.line_items, { ...defaultItem }] });
    const removeItem = (idx) => setForm({ ...form, line_items: form.line_items.filter((_, i) => i !== idx) });

    const generateCoverLetter = async () => {
        setGeneratingAI(true);
        try {
            const { subtotal, total } = calcTotals(form.line_items, form.tax_rate);
            const res = await axios.post(`${API}/ai/generate-proposal-content`, {
                account_name: form.account_name || accounts.find(a => a.account_id === form.account_id)?.name || 'Client',
                products: form.line_items,
                total,
            }, { headers: getAuthHeader() });
            setForm(prev => ({ ...prev, cover_letter: res.data.cover_letter }));
            toast.success('AI cover letter generated!');
        } catch { toast.error('AI generation failed'); }
        finally { setGeneratingAI(false); }
    };

    const handleSave = async (sendProposal = false) => {
        if (!form.title) return toast.error('Proposal title required');
        setLoading(true);
        try {
            const payload = {
                ...form,
                status: sendProposal ? 'sent' : form.status || 'draft',
                account_name: form.account_name || accounts.find(a => a.account_id === form.account_id)?.name,
            };
            let res;
            if (isEdit) res = await axios.put(`${API}/proposals/${proposalId}`, payload, { headers: getAuthHeader() });
            else res = await axios.post(`${API}/proposals`, payload, { headers: getAuthHeader() });
            toast.success(sendProposal ? 'Proposal sent!' : 'Proposal saved!');
            navigate(`/proposals/${res.data.proposal_id}`);
        } catch { toast.error('Failed to save'); }
        finally { setLoading(false); }
    };

    const { subtotal, tax, total } = calcTotals(form.line_items, form.tax_rate);
    const formatVal = v => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0);

    return (
        <div className="space-y-6 max-w-5xl animate-fade-in">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => prefillAccountId ? navigate(`/accounts/${prefillAccountId}`) : navigate('/proposals')}
                        className="p-2 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-600">
                        <ChevronLeft className="w-5 h-5" />
                    </button>
                    <div>
                        <h1 className="text-xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {isEdit ? 'Edit Proposal' : 'Create Proposal'}
                        </h1>
                        {prefillAccountName && !isEdit && (
                            <p className="text-sm text-stone-700 flex items-center gap-1 mt-0.5">
                                <Building2 className="w-3.5 h-3.5" />
                                For {prefillAccountName}
                            </p>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={() => handleSave(false)} disabled={loading} data-testid="save-proposal-btn"
                        className="flex items-center gap-2 px-4 py-2 text-sm border border-stone-200 rounded-lg hover:bg-stone-50 text-stone-700 disabled:opacity-50">
                        <Save className="w-4 h-4" />{loading ? 'Saving...' : 'Save Draft'}
                    </button>
                    <button onClick={() => handleSave(true)} disabled={loading} data-testid="send-proposal-btn"
                        className="flex items-center gap-2 px-4 py-2 bg-stone-900 text-white text-sm font-medium rounded-lg hover:bg-stone-800 disabled:opacity-50 shadow-sm">
                        <Send className="w-4 h-4" /> Send Proposal
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-3 gap-6">
                {/* Main form */}
                <div className="col-span-2 space-y-6">
                    {/* Basic info */}
                    <div className="bg-white rounded-xl border border-stone-200 p-6">
                        <h3 className="font-semibold text-stone-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Proposal Details</h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-2">
                                <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Proposal Title *</label>
                                <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Enterprise License Proposal 2025"
                                    data-testid="proposal-title-input"
                                    className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-400" />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Company</label>
                                {prefillAccountId && !isEdit ? (
                                    <div className="flex items-center gap-2 px-3 py-2 bg-stone-100 border border-stone-300 rounded-lg">
                                        <Building2 className="w-4 h-4 text-stone-600 flex-shrink-0" />
                                        <span className="text-sm font-medium text-stone-900">{prefillAccountName}</span>
                                        <span className="ml-auto text-xs text-stone-500 bg-stone-100 px-1.5 py-0.5 rounded-full">pre-filled</span>
                                    </div>
                                ) : (
                                <select value={form.account_id || ''} onChange={e => {
                                    const acc = accounts.find(a => a.account_id === e.target.value);
                                    setForm({ ...form, account_id: e.target.value, account_name: acc?.name || '' });
                                }} data-testid="proposal-account-select"
                                    className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none">
                                    <option value="">Select account...</option>
                                    {accounts.map(a => <option key={a.account_id} value={a.account_id}>{a.name}</option>)}
                                </select>
                                )}
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Valid Until</label>
                                <input type="date" value={form.valid_until || ''} onChange={e => setForm({ ...form, valid_until: e.target.value })}
                                    data-testid="proposal-valid-until-input"
                                    className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none" />
                            </div>
                        </div>
                    </div>

                    {/* Line Items */}
                    <div className="bg-white rounded-xl border border-stone-200 p-6">
                        <h3 className="font-semibold text-stone-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Products & Services</h3>
                        <div className="space-y-3">
                            <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-stone-400 uppercase tracking-wider px-1">
                                <div className="col-span-4">Item</div>
                                <div className="col-span-2">Qty</div>
                                <div className="col-span-2">Unit Price</div>
                                <div className="col-span-2">Discount %</div>
                                <div className="col-span-1">Total</div>
                                <div />
                            </div>
                            {form.line_items.map((item, idx) => (
                                <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                                    <div className="col-span-4">
                                        <input value={item.product_name} onChange={e => updateItem(idx, 'product_name', e.target.value)} placeholder="Product name"
                                            data-testid={`line-item-name-${idx}`}
                                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                                    </div>
                                    <div className="col-span-2">
                                        <input type="number" value={item.quantity} onChange={e => updateItem(idx, 'quantity', parseFloat(e.target.value) || 0)} min="1"
                                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                                    </div>
                                    <div className="col-span-2">
                                        <input type="number" value={item.unit_price} onChange={e => updateItem(idx, 'unit_price', parseFloat(e.target.value) || 0)} min="0"
                                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                                    </div>
                                    <div className="col-span-2">
                                        <input type="number" value={item.discount_percent || 0} onChange={e => updateItem(idx, 'discount_percent', parseFloat(e.target.value) || 0)} min="0" max="100"
                                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                                    </div>
                                    <div className="col-span-1 text-sm font-medium text-stone-700">{formatVal(item.total)}</div>
                                    <div>
                                        {form.line_items.length > 1 && (
                                            <button onClick={() => removeItem(idx)} className="p-1.5 rounded-lg hover:bg-red-50 text-stone-400 hover:text-red-500 transition-colors">
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <button onClick={addItem} data-testid="add-line-item-btn"
                            className="mt-3 flex items-center gap-2 text-sm text-stone-700 hover:text-stone-900">
                            <Plus className="w-4 h-4" /> Add item
                        </button>
                    </div>

                    {/* Cover Letter */}
                    <div className="bg-white rounded-xl border border-stone-200 p-6" style={{ borderTop: '3px solid transparent', borderImage: 'linear-gradient(135deg, #6366f1, #a855f7) 1' }}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Cover Letter</h3>
                            <button onClick={generateCoverLetter} disabled={generatingAI} data-testid="ai-cover-letter-btn"
                                className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gradient-to-r from-stone-900 to-stone-700 text-white rounded-lg hover:from-stone-800 hover:to-stone-800 disabled:opacity-50">
                                <Sparkles className="w-3.5 h-3.5" />
                                {generatingAI ? 'Generating...' : 'AI Generate'}
                            </button>
                        </div>
                        <textarea value={form.cover_letter || ''} onChange={e => setForm({ ...form, cover_letter: e.target.value })} rows={6}
                            placeholder="Write or generate an AI cover letter..."
                            data-testid="proposal-cover-letter-input"
                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20 resize-none" />
                    </div>

                    {/* Notes & Terms */}
                    <div className="bg-white rounded-xl border border-stone-200 p-6">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Notes</label>
                                <textarea value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3}
                                    className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none resize-none" />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Terms & Conditions</label>
                                <textarea value={form.terms || ''} onChange={e => setForm({ ...form, terms: e.target.value })} rows={3}
                                    className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none resize-none" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Sidebar - Summary */}
                <div className="space-y-4">
                    <div className="bg-white rounded-xl border border-stone-200 p-5 sticky top-4">
                        <h3 className="font-semibold text-stone-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Financial Summary</h3>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between text-stone-600">
                                <span>Subtotal</span>
                                <span className="font-medium">{formatVal(subtotal)}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-stone-600">Tax Rate (%)</span>
                                <input type="number" value={form.tax_rate || 0} onChange={e => setForm({ ...form, tax_rate: parseFloat(e.target.value) || 0 })} min="0" max="100"
                                    className="w-16 px-2 py-1 text-xs border border-stone-200 rounded text-right focus:outline-none focus:border-stone-400" />
                            </div>
                            <div className="flex justify-between text-stone-600">
                                <span>Tax</span>
                                <span>{formatVal(tax)}</span>
                            </div>
                            <div className="flex justify-between pt-2 border-t border-stone-200 font-bold text-stone-900">
                                <span>Total</span>
                                <span className="text-stone-700 text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>{formatVal(total)}</span>
                            </div>
                        </div>

                        <div className="mt-4 pt-4 border-t border-stone-200">
                            <div className="text-xs text-stone-400">
                                <p>{form.line_items.length} line items</p>
                                {form.account_name && <p className="mt-1">{form.account_name}</p>}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
